// client/src/hooks/useServicos.js - VERSÃO 100% COMPLETA COM SUPORTE A POOL E RECURSOS OBRIGATÓRIOS
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import useAlert from './useAlert';

const SERVICOS_QUERY_KEY = 'servicos';
const SERVICO_DETAIL_QUERY_KEY = 'servico';
const USER_SERVICOS_QUERY_KEY = 'user-servicos';
const SERVICOS_EXCLUIDOS_QUERY_KEY = 'servicos-excluidos';

// ✅ CORREÇÃO CRÍTICA: Função sanitizeParams agora é à prova de undefined
const sanitizeParams = (params) => {
  if (!params || typeof params !== 'object') {
    return {};
  }
  
  const sanitized = {};
  
  Object.keys(params).forEach(key => {
    const value = params[key];
    
    if (value === undefined || value === null || value === '') {
      return;
    }
    
    if (typeof value === 'boolean') {
      sanitized[key] = value;
    } else if (typeof value === 'number') {
      sanitized[key] = value;
    } else if (Array.isArray(value)) {
      if (value.length > 0) {
        sanitized[key] = value;
      }
    } else if (typeof value === 'string') {
      sanitized[key] = value;
    }
    
    if (key === 'precoMinimo' && (value === 0 || value === '0')) {
      sanitized[key] = 0;
    }
    if (key === 'precoMaximo' && (value === 0 || value === '0')) {
      sanitized[key] = 0;
    }
  });
  
  console.log('🔧 [SANITIZE_PARAMS] Parâmetros sanitizados (Flat):', sanitized);
  return sanitized;
};

// Preparar dados do serviço com cache e soft delete (incluindo novos campos de pool)
// Contrato canônico do payload — ver bloco de documentação em
// server/controllers/servicosController.js (CONTRATO CANÔNICO —
// ServicoPayload). metadata/configAnamnese são subobjetos: o backend faz
// merge raso com o valor atual, mas prefira sempre enviar o objeto completo
// (como este preparador já faz via spread de servicoData).
const prepararDadosServico = (servicoData) => {
  console.log("🔄 [PREPARADOR FRONTEND] Preparando dados do serviço:", {
    dadosRecebidos: servicoData,
    idsDisponiveis: {
      _id: servicoData?._id,
      id: servicoData?.id,
      servicoId: servicoData?.servicoId
    }
  });
  
  const dadosPreparados = { ...servicoData };
  
  if (!dadosPreparados._id && dadosPreparados.id) {
    dadosPreparados._id = dadosPreparados.id;
  }
  
  if (!dadosPreparados.id && dadosPreparados._id) {
    dadosPreparados.id = dadosPreparados._id;
  }

  if (dadosPreparados.categoria) {
    if (typeof dadosPreparados.categoria === 'string' && 
        (dadosPreparados.categoria.trim() === '' || 
         !/^[0-9a-fA-F]{24}$/.test(dadosPreparados.categoria))) {
      dadosPreparados.categoria = null;
    }
    else if (typeof dadosPreparados.categoria === 'object' && dadosPreparados.categoria._id) {
      dadosPreparados.categoria = dadosPreparados.categoria._id;
    }
  }
  
  // ✅ NOVO: Processar recursos obrigatórios
  dadosPreparados.recursosObrigatorios = Array.isArray(dadosPreparados.recursosObrigatorios) 
    ? dadosPreparados.recursosObrigatorios.map(r => r._id || r)
    : dadosPreparados.recursosObrigatorios || [];
  
  // ✅ NOVO: Processar pool de recursos
  if (dadosPreparados.poolRecursoId && typeof dadosPreparados.poolRecursoId === 'object') {
    dadosPreparados.poolRecursoId = dadosPreparados.poolRecursoId._id || dadosPreparados.poolRecursoId.id || null;
  }
  
  dadosPreparados.unidadesDetalhes = Array.isArray(dadosPreparados.unidadesDetalhes) 
    ? dadosPreparados.unidadesDetalhes.map(ud => ({
        unidade: ud.unidade?._id || ud.unidade,
        ativo: ud.ativo !== undefined ? ud.ativo : true,
        precoPersonalizado: ud.precoPersonalizado || null,
        recursosVinculados: Array.isArray(ud.recursosVinculados) 
          ? ud.recursosVinculados.map(r => r._id || r)
          : [],
        tempoPreparacao: ud.tempoPreparacao || 0,
        tempoLimpeza: ud.tempoLimpeza || 0,
        disponivelOnline: ud.disponivelOnline !== undefined ? ud.disponivelOnline : false,
        observacoes: ud.observacoes || ''
      }))
    : [];
  
  dadosPreparados.recursosVinculados = Array.isArray(dadosPreparados.recursosVinculados) 
    ? dadosPreparados.recursosVinculados.map(r => r._id || r)
    : [];
  
  if (!dadosPreparados.cache) {
    dadosPreparados.cache = {
      precoMinimo: 0,
      unidadesAtivasCount: 0,
      profissionaisAtivosCount: 0,
      recursosAtivosCount: 0,
      duracaoTotalMedia: 0,
      ultimaAtualizacao: new Date().toISOString(),
      hashDados: ''
    };
  }
  
  delete dadosPreparados.profissionaisHabilitados;
  delete dadosPreparados.status;
  delete dadosPreparados.__v;
  delete dadosPreparados.createdAt;
  delete dadosPreparados.updatedAt;
  delete dadosPreparados.deletado;
  delete dadosPreparados.deletadoEm;
  delete dadosPreparados.deletadoPor;
  delete dadosPreparados.motivoExclusao;
  
  return dadosPreparados;
};

// Processar resposta da API com estrutura padronizada
const processApiResponse = (responseData) => {
  if (!responseData) {
    return { 
      success: false, 
      data: [], 
      count: 0, 
      totalCount: 0, 
      pagination: {} 
    };
  }

  if (responseData.success !== undefined && responseData.data !== undefined) {
    return {
      success: responseData.success,
      data: Array.isArray(responseData.data) ? responseData.data : [responseData.data],
      count: responseData.count || 0,
      totalCount: responseData.totalCount || responseData.count || 0,
      pagination: responseData.pagination || {
        page: 1,
        limit: 10,
        totalPages: 1,
        totalCount: responseData.count || 0
      },
      message: responseData.message
    };
  }

  if (Array.isArray(responseData)) {
    return {
      success: true,
      data: responseData,
      count: responseData.length,
      totalCount: responseData.length,
      pagination: {
        page: 1,
        limit: responseData.length,
        totalPages: 1,
        totalCount: responseData.length
      }
    };
  }

  return {
    success: true,
    data: responseData.data || responseData || [],
    count: Array.isArray(responseData.data) ? responseData.data.length : 1,
    totalCount: Array.isArray(responseData.data) ? responseData.data.length : 1,
    pagination: responseData.pagination || {}
  };
};

// ==============================================================================
// HOOKS DE LEITURA (QUERIES)
// ==============================================================================

export const useServicosExcluidos = (params = {}) => {
  const { enabled = true, ...queryOptions } = params;
  
  return useQuery({
    queryKey: [SERVICOS_EXCLUIDOS_QUERY_KEY, params],
    queryFn: async () => {
      console.log('🔧 [USE_SERVICOS_EXCLUIDOS] Buscando serviços excluídos:', params);

      const cleanParams = sanitizeParams(params);

      try {
        const response = await apiClient.get('/servicos/excluidos', { 
          params: cleanParams,
          timeout: 15000
        });
        
        return processApiResponse(response.data);
        
      } catch (error) {
        console.error('❌ [USE_SERVICOS_EXCLUIDOS] Erro:', error);
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: enabled && !!params?.account,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 1,
    ...queryOptions
  });
};

export const useServicoCacheInfo = (servicoId, options = {}) => {
  return useQuery({
    queryKey: ['servico-cache', servicoId],
    queryFn: async () => {
      if (!servicoId) {
        throw new Error('Serviço ID é obrigatório');
      }
      
      try {
        const response = await apiClient.get(`/servicos/${servicoId}/cache`);
        return response.data;
      } catch (error) {
        console.error('❌ [USE_SERVICO_CACHE_INFO] Erro:', error);
        throw error;
      }
    },
    enabled: !!servicoId && (options.enabled !== false),
    staleTime: 1000 * 30,
    retry: 1,
    ...options
  });
};

export const useServicoStatus = (servicoId, options = {}) => {
  return useQuery({
    queryKey: ['servico-status', servicoId],
    queryFn: async () => {
      if (!servicoId) {
        throw new Error('Serviço ID é obrigatório');
      }
      
      try {
        const response = await apiClient.get(`/servicos/${servicoId}/status`);
        return response.data;
      } catch (error) {
        console.error('❌ [USE_SERVICO_STATUS] Erro:', error);
        throw error;
      }
    },
    enabled: !!servicoId && (options.enabled !== false),
    staleTime: 1000 * 60 * 2,
    retry: 1,
    ...options
  });
};

// ✅ CORREÇÃO CRÍTICA: Hook useServicos principal COM SELECT EXPLÍCITO
export const useServicos = (params = {}, options = {}) => {
  const { setAlert } = useAlert();
  const { enabled = true, ...queryOptions } = options;

  const hasValidAccount = !!params?.account && 
    /^[0-9a-fA-F]{24}$/.test(params.account);

  return useQuery({
    queryKey: [SERVICOS_QUERY_KEY, params],
    queryFn: async () => {
      console.log('🔧 [USE_SERVICOS] Buscando serviços com params:', params);

      if (!params || Object.keys(params).length === 0 || !hasValidAccount) {
        console.log('ℹ️ [USE_SERVICOS] Sem accountId válido, retornando estrutura vazia');
        return {
          success: true,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {
            page: 1,
            limit: 10,
            totalPages: 0,
            totalCount: 0
          }
        };
      }

      const cleanParams = sanitizeParams(params);

      if (cleanParams.includeDeleted === undefined) {
        cleanParams.includeDeleted = false;
      }

      // ✅ CORREÇÃO CRÍTICA: Adicionar select para garantir campos importantes
      if (!cleanParams.select) {
        cleanParams.select = 'nome descricao duracaoSessaoEmMinutos agendavel controlaSessao ativo ' +
                            'tipoDePrecificacao valorFixo pacotesDePreco metadata ' +
                            'imagemPrincipal imagens recursosVinculados recursosObrigatorios ' +
                            'usaPoolRecurso poolRecursoId quantidadeRecursosNecessaria ' +
                            'unidadesDetalhes categoria visivelParaCliente precoVisivelParaCliente requerAnamnese';
      } else if (!cleanParams.select.includes('duracaoSessaoEmMinutos')) {
        cleanParams.select = `${cleanParams.select},duracaoSessaoEmMinutos`;
      }
      // ✅ NOVO: Garantir campos de pool no select
      if (!cleanParams.select.includes('usaPoolRecurso')) {
        cleanParams.select = `${cleanParams.select},usaPoolRecurso,poolRecursoId,quantidadeRecursosNecessaria,recursosObrigatorios`;
      }

      if (!cleanParams.populate) {
        cleanParams.populate = 'unidadesDetalhes.unidade,categoria,recursosVinculados,recursosObrigatorios,poolRecursoId';
      } else if (!cleanParams.populate.includes('unidadesDetalhes.unidade')) {
        cleanParams.populate = `${cleanParams.populate},unidadesDetalhes.unidade,recursosObrigatorios,poolRecursoId`;
      }

      Object.keys(cleanParams).forEach(key => {
        const value = cleanParams[key];
        if (value === 'true' || value === 'false') {
          cleanParams[key] = value === 'true';
        }
      });

      if (cleanParams.precoMinimo !== undefined) {
        if (typeof cleanParams.precoMinimo === 'string') {
          cleanParams.precoMinimo = parseFloat(cleanParams.precoMinimo) || 0;
        }
      }

      if (cleanParams.precoMaximo !== undefined) {
        if (typeof cleanParams.precoMaximo === 'string') {
          cleanParams.precoMaximo = parseFloat(cleanParams.precoMaximo) || 0;
        }
      }

      if (Array.isArray(cleanParams.categoria)) {
        if (cleanParams.categoria.length === 1) {
          cleanParams.categoria = cleanParams.categoria[0];
        }
      }

      console.log('🔧 [USE_SERVICOS] Parâmetros finais para API:', cleanParams);
      
      try {
        const response = await apiClient.get('/servicos', { 
          params: cleanParams,
          timeout: 15000,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        const processedResponse = processApiResponse(response.data);
        
        if (processedResponse.data && processedResponse.data.length > 0) {
          console.log('🔍 [USE_SERVICOS] Primeiro serviço - usaPoolRecurso:', 
            processedResponse.data[0].usaPoolRecurso);
          console.log('🔍 [USE_SERVICOS] Primeiro serviço - recursosObrigatorios:', 
            processedResponse.data[0].recursosObrigatorios?.length);
        }
        
        processedResponse.appliedFilters = cleanParams;
        processedResponse.timestamp = new Date().toISOString();
        
        return processedResponse;
        
      } catch (error) {
        console.error('❌ [USE_SERVICOS] Erro:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          params: cleanParams
        });
        
        setAlert(`Erro ao buscar serviços: ${error.message}`, 'danger');
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {
            page: parseInt(params.page) || 1,
            limit: parseInt(params.limit) || 10,
            totalPages: 0,
            totalCount: 0
          },
          error: error.message,
          appliedFilters: cleanParams
        };
      }
    },
    enabled: enabled && hasValidAccount,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 2,
    ...queryOptions
  });
};

export const useServicosPorConta = (accountId, params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  const hasValidAccount = !!accountId && /^[0-9a-fA-F]{24}$/.test(accountId);

  return useQuery({
    queryKey: [SERVICOS_QUERY_KEY, 'conta', accountId, params],
    queryFn: async () => {
      if (!hasValidAccount) {
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: 'Account ID é obrigatório'
        };
      }

      const queryParams = sanitizeParams({
        account: accountId,
        includeDeleted: params.includeDeleted || false,
        ...params
      });

      // ✅ CORREÇÃO CRÍTICA: Adicionar select para garantir campos importantes
      if (!queryParams.select) {
        queryParams.select = 'nome descricao duracaoSessaoEmMinutos agendavel controlaSessao ativo ' +
                            'tipoDePrecificacao valorFixo pacotesDePreco metadata ' +
                            'imagemPrincipal imagens recursosVinculados recursosObrigatorios ' +
                            'usaPoolRecurso poolRecursoId quantidadeRecursosNecessaria ' +
                            'unidadesDetalhes categoria';
      } else if (!queryParams.select.includes('duracaoSessaoEmMinutos')) {
        queryParams.select = `${queryParams.select},duracaoSessaoEmMinutos`;
      }
      if (!queryParams.select.includes('usaPoolRecurso')) {
        queryParams.select = `${queryParams.select},usaPoolRecurso,poolRecursoId,quantidadeRecursosNecessaria,recursosObrigatorios`;
      }

      if (!queryParams.populate) {
        queryParams.populate = 'unidadesDetalhes.unidade,categoria,recursosVinculados,recursosObrigatorios,poolRecursoId';
      } else if (!queryParams.populate.includes('unidadesDetalhes.unidade')) {
        queryParams.populate = `${queryParams.populate},unidadesDetalhes.unidade,recursosObrigatorios,poolRecursoId`;
      }

      console.log('🔧 [USE_SERVICOS_POR_CONTA] Buscando serviços da conta:', accountId, queryParams);
      
      try {
        const response = await apiClient.get('/servicos', { 
          params: queryParams,
          timeout: 15000
        });
        
        const processedResponse = processApiResponse(response.data);
        processedResponse.appliedFilters = queryParams;
        
        return processedResponse;
        
      } catch (error) {
        console.error('❌ [USE_SERVICOS_POR_CONTA] Erro:', error);
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message,
          appliedFilters: queryParams
        };
      }
    },
    enabled: enabled && hasValidAccount,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 1,
    ...queryOptions
  });
};

export const useServico = (servicoId, options = {}) => {
  const { enabled = true, includeDeleted = false, ...queryOptions } = options;

  return useQuery({
    queryKey: [SERVICO_DETAIL_QUERY_KEY, servicoId],
    queryFn: async () => {
      if (!servicoId) {
        return null;
      }
      
      try {
        const response = await apiClient.get(`/servicos/${servicoId}`, { 
          timeout: 10000,
          params: { includeDeleted }
        });
        
        const responseData = response.data;
        const servico = responseData?.data || responseData || null;
        
        return servico;
        
      } catch (error) {
        console.error('❌ [USE_SERVICO] Erro:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        
        if (error.response?.status === 404) {
          return null;
        }
        
        throw error;
      }
    },
    enabled: enabled && !!servicoId,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

// ✅ HOOKS ESPECIALIZADOS
export const useProfissionaisServico = (servicoId, options = {}) => {
  const { unidadeId, accountId, enabled = true } = options;
  
  return useQuery({
    queryKey: ['profissionais-servico', servicoId, unidadeId, accountId],
    queryFn: async () => {
      if (!servicoId) {
        throw new Error('Serviço ID é obrigatório');
      }
      
      const params = sanitizeParams({
        ...(unidadeId && { unidadeId }),
        ...(accountId && { accountId })
      });
      
      try {
        const response = await apiClient.get(`/servicos/${servicoId}/profissionais`, { params });
        return processApiResponse(response.data);
        
      } catch (error) {
        console.error('❌ [USE_PROFISSIONAIS_SERVICO] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && !!servicoId,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
};

export const useUserServicos = (filters = {}, options = {}) => {
  const { userId, servicoId, accountId, unidadeId, status = 'ativo' } = filters;
  const { enabled = true, ...queryOptions } = options;
  
  const hasValidParams = !!(userId || servicoId || accountId);
  
  return useQuery({
    queryKey: [USER_SERVICOS_QUERY_KEY, userId, servicoId, accountId, unidadeId, status],
    queryFn: async () => {
      const params = sanitizeParams({
        ...(userId && { user: userId }),
        ...(servicoId && { servico: servicoId }),
        ...(accountId && { account: accountId }),
        ...(unidadeId && { unidade: unidadeId }),
        status
      });
      
      try {
        const response = await apiClient.get('/relationships/user-servicos', { params });
        return processApiResponse(response.data);
        
      } catch (error) {
        console.error('❌ [USE_USER_SERVICOS] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && hasValidParams,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

export const useCalcularComissao = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();
  
  return useMutation({
    mutationFn: async ({ userServicoId, valorServico, valorTotal }) => {
      if (!userServicoId || !valorServico) {
        throw new Error('ID do vínculo e valor do serviço são obrigatórios');
      }
      
      const response = await apiClient.post(`/relationships/user-servicos/${userServicoId}/calcular-comissao`, {
        valorServico,
        valorTotal
      });
      
      return response.data;
    },
    onSuccess: (data) => {
      console.log('✅ [USE_CALCULAR_COMISSAO] Comissão calculada:', data);
    },
    onError: (error) => {
      console.error('❌ [USE_CALCULAR_COMISSAO] Erro:', error);
      setAlert(error.response?.data?.error || error.message, 'danger');
      throw error;
    }
  });
};

// ✅ HOOKS DE BUSCA ESPECIALIZADA
export const useBuscarServicos = (filters = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['servicos-busca', filters],
    queryFn: async () => {
      const cleanParams = sanitizeParams(filters);
      
      // ✅ CORREÇÃO CRÍTICA: Adicionar select para garantir campos importantes
      if (!cleanParams.select) {
        cleanParams.select = 'nome descricao duracaoSessaoEmMinutos agendavel controlaSessao ativo ' +
                            'tipoDePrecificacao valorFixo pacotesDePreco metadata ' +
                            'imagemPrincipal imagens recursosVinculados recursosObrigatorios ' +
                            'usaPoolRecurso poolRecursoId quantidadeRecursosNecessaria ' +
                            'unidadesDetalhes categoria';
      } else if (!cleanParams.select.includes('duracaoSessaoEmMinutos')) {
        cleanParams.select = `${cleanParams.select},duracaoSessaoEmMinutos`;
      }
      if (!cleanParams.select.includes('usaPoolRecurso')) {
        cleanParams.select = `${cleanParams.select},usaPoolRecurso,poolRecursoId,quantidadeRecursosNecessaria,recursosObrigatorios`;
      }

      if (!cleanParams.populate) {
        cleanParams.populate = 'unidadesDetalhes.unidade,categoria,recursosVinculados,recursosObrigatorios,poolRecursoId';
      } else if (!cleanParams.populate.includes('unidadesDetalhes.unidade')) {
        cleanParams.populate = `${cleanParams.populate},unidadesDetalhes.unidade,recursosObrigatorios,poolRecursoId`;
      }
      
      try {
        const response = await apiClient.get('/servicos', { 
          params: cleanParams,
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        
        return processApiResponse(response.data);
        
      } catch (error) {
        console.error('❌ [USE_BUSCAR_SERVICOS] Erro na requisição:', error);
        throw error;
      }
    },
    enabled: enabled && !!filters?.account,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

export const useServicosPorUnidade = (unidadeId, params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['servicos-unidade', unidadeId, params],
    queryFn: async () => {
      if (!unidadeId) {
        throw new Error('Unidade ID é obrigatório');
      }
      
      try {
        const response = await apiClient.get(`/servicos/unidade/${unidadeId}`, {
          params: sanitizeParams(params)
        });
        
        return processApiResponse(response.data);
          
      } catch (error) {
        console.error('❌ [USE_SERVICOS_POR_UNIDADE] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && !!unidadeId,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

export const useServicosExternosPorLocalizacao = (distanciaMaximaKm, unidadeId = null, params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['servicos-externos-localizacao', distanciaMaximaKm, unidadeId, params],
    queryFn: async () => {
      if (!distanciaMaximaKm) {
        throw new Error('Distância máxima é obrigatória');
      }
      
      const queryParams = sanitizeParams({
        distanciaMaxima: distanciaMaximaKm,
        ...(unidadeId && { unidadeId }),
        ...params
      });
      
      try {
        const response = await apiClient.get('/servicos/externos/localizacao', { params: queryParams });
        return processApiResponse(response.data);
                    
      } catch (error) {
        console.error('❌ [USE_SERVICOS_EXTERNOS_LOCALIZACAO] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && !!distanciaMaximaKm,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

export const useServicosAgendaveis = (params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['servicos-agendaveis', params],
    queryFn: async () => {
      const queryParams = sanitizeParams({
        agendavel: true,
        ativo: true,
        visivelParaCliente: true,
        ...params
      });
      
      // ✅ CORREÇÃO CRÍTICA: Adicionar select para garantir campos importantes
      if (!queryParams.select) {
        queryParams.select = 'nome descricao duracaoSessaoEmMinutos agendavel controlaSessao ativo ' +
                            'tipoDePrecificacao valorFixo pacotesDePreco metadata ' +
                            'imagemPrincipal imagens recursosVinculados recursosObrigatorios ' +
                            'usaPoolRecurso poolRecursoId quantidadeRecursosNecessaria ' +
                            'unidadesDetalhes categoria';
      } else if (!queryParams.select.includes('duracaoSessaoEmMinutos')) {
        queryParams.select = `${queryParams.select},duracaoSessaoEmMinutos`;
      }
      if (!queryParams.select.includes('usaPoolRecurso')) {
        queryParams.select = `${queryParams.select},usaPoolRecurso,poolRecursoId,quantidadeRecursosNecessaria,recursosObrigatorios`;
      }

      if (!queryParams.populate) {
        queryParams.populate = 'unidadesDetalhes.unidade,categoria,recursosVinculados,recursosObrigatorios,poolRecursoId';
      } else if (!queryParams.populate.includes('unidadesDetalhes.unidade')) {
        queryParams.populate = `${queryParams.populate},unidadesDetalhes.unidade,recursosObrigatorios,poolRecursoId`;
      }
      
      try {
        const response = await apiClient.get('/servicos', { params: queryParams });
        return processApiResponse(response.data);
          
      } catch (error) {
        console.error('❌ [USE_SERVICOS_AGENDAVEIS] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && !!params?.account,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

export const useServicosExternos = (params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['servicos-externos', params],
    queryFn: async () => {
      const queryParams = sanitizeParams({
        ativo: true,
        tipoServico: 'externo',
        ...params
      });
      
      // ✅ CORREÇÃO CRÍTICA: Adicionar select para garantir campos importantes
      if (!queryParams.select) {
        queryParams.select = 'nome descricao duracaoSessaoEmMinutos agendavel controlaSessao ativo ' +
                            'tipoDePrecificacao valorFixo pacotesDePreco metadata ' +
                            'imagemPrincipal imagens recursosVinculados recursosObrigatorios ' +
                            'usaPoolRecurso poolRecursoId quantidadeRecursosNecessaria ' +
                            'unidadesDetalhes categoria';
      } else if (!queryParams.select.includes('duracaoSessaoEmMinutos')) {
        queryParams.select = `${queryParams.select},duracaoSessaoEmMinutos`;
      }
      if (!queryParams.select.includes('usaPoolRecurso')) {
        queryParams.select = `${queryParams.select},usaPoolRecurso,poolRecursoId,quantidadeRecursosNecessaria,recursosObrigatorios`;
      }

      if (!queryParams.populate) {
        queryParams.populate = 'unidadesDetalhes.unidade,categoria,recursosVinculados,recursosObrigatorios,poolRecursoId';
      } else if (!queryParams.populate.includes('unidadesDetalhes.unidade')) {
        queryParams.populate = `${queryParams.populate},unidadesDetalhes.unidade,recursosObrigatorios,poolRecursoId`;
      }
      
      try {
        const response = await apiClient.get('/servicos', { params: queryParams });
        return processApiResponse(response.data);
          
      } catch (error) {
        console.error('❌ [USE_SERVICOS_EXTERNOS] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && !!params?.account,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

export const useServicosInternos = (params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['servicos-internos', params],
    queryFn: async () => {
      const queryParams = sanitizeParams({
        ativo: true,
        tipoServico: 'interno',
        ...params
      });
      
      // ✅ CORREÇÃO CRÍTICA: Adicionar select para garantir campos importantes
      if (!queryParams.select) {
        queryParams.select = 'nome descricao duracaoSessaoEmMinutos agendavel controlaSessao ativo ' +
                            'tipoDePrecificacao valorFixo pacotesDePreco metadata ' +
                            'imagemPrincipal imagens recursosVinculados recursosObrigatorios ' +
                            'usaPoolRecurso poolRecursoId quantidadeRecursosNecessaria ' +
                            'unidadesDetalhes categoria';
      } else if (!queryParams.select.includes('duracaoSessaoEmMinutos')) {
        queryParams.select = `${queryParams.select},duracaoSessaoEmMinutos`;
      }
      if (!queryParams.select.includes('usaPoolRecurso')) {
        queryParams.select = `${queryParams.select},usaPoolRecurso,poolRecursoId,quantidadeRecursosNecessaria,recursosObrigatorios`;
      }

      if (!queryParams.populate) {
        queryParams.populate = 'unidadesDetalhes.unidade,categoria,recursosVinculados,recursosObrigatorios,poolRecursoId';
      } else if (!queryParams.populate.includes('unidadesDetalhes.unidade')) {
        queryParams.populate = `${queryParams.populate},unidadesDetalhes.unidade,recursosObrigatorios,poolRecursoId`;
      }
      
      try {
        const response = await apiClient.get('/servicos', { params: queryParams });
        return processApiResponse(response.data);
          
      } catch (error) {
        console.error('❌ [USE_SERVICOS_INTERNOS] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && !!params?.account,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

export const useServicosOnline = (params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['servicos-online', params],
    queryFn: async () => {
      const queryParams = sanitizeParams({
        ativo: true,
        agendavel: true,
        visivelParaCliente: true,
        disponivelOnline: true,
        ...params
      });
      
      // ✅ CORREÇÃO CRÍTICA: Adicionar select para garantir campos importantes
      if (!queryParams.select) {
        queryParams.select = 'nome descricao duracaoSessaoEmMinutos agendavel controlaSessao ativo ' +
                            'tipoDePrecificacao valorFixo pacotesDePreco metadata ' +
                            'imagemPrincipal imagens recursosVinculados recursosObrigatorios ' +
                            'usaPoolRecurso poolRecursoId quantidadeRecursosNecessaria ' +
                            'unidadesDetalhes categoria';
      } else if (!queryParams.select.includes('duracaoSessaoEmMinutos')) {
        queryParams.select = `${queryParams.select},duracaoSessaoEmMinutos`;
      }
      if (!queryParams.select.includes('usaPoolRecurso')) {
        queryParams.select = `${queryParams.select},usaPoolRecurso,poolRecursoId,quantidadeRecursosNecessaria,recursosObrigatorios`;
      }

      if (!queryParams.populate) {
        queryParams.populate = 'unidadesDetalhes.unidade,categoria,recursosVinculados,recursosObrigatorios,poolRecursoId';
      } else if (!queryParams.populate.includes('unidadesDetalhes.unidade')) {
        queryParams.populate = `${queryParams.populate},unidadesDetalhes.unidade,recursosObrigatorios,poolRecursoId`;
      }
      
      try {
        const response = await apiClient.get('/servicos/buscar', { params: queryParams });
        return processApiResponse(response.data);
          
      } catch (error) {
        console.error('❌ [USE_SERVICOS_ONLINE] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && !!params?.account,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

export const useServicosComAnamnese = (params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['servicos-anamnese', params],
    queryFn: async () => {
      const queryParams = sanitizeParams({
        ativo: true,
        requerAnamnese: true,
        ...params
      });
      
      // ✅ CORREÇÃO CRÍTICA: Adicionar select para garantir campos importantes
      if (!queryParams.select) {
        queryParams.select = 'nome descricao duracaoSessaoEmMinutos agendavel controlaSessao ativo ' +
                            'tipoDePrecificacao valorFixo pacotesDePreco metadata ' +
                            'imagemPrincipal imagens recursosVinculados recursosObrigatorios ' +
                            'usaPoolRecurso poolRecursoId quantidadeRecursosNecessaria ' +
                            'unidadesDetalhes categoria';
      } else if (!queryParams.select.includes('duracaoSessaoEmMinutos')) {
        queryParams.select = `${queryParams.select},duracaoSessaoEmMinutos`;
      }
      if (!queryParams.select.includes('usaPoolRecurso')) {
        queryParams.select = `${queryParams.select},usaPoolRecurso,poolRecursoId,quantidadeRecursosNecessaria,recursosObrigatorios`;
      }

      if (!queryParams.populate) {
        queryParams.populate = 'unidadesDetalhes.unidade,categoria,recursosVinculados,recursosObrigatorios,poolRecursoId';
      } else if (!queryParams.populate.includes('unidadesDetalhes.unidade')) {
        queryParams.populate = `${queryParams.populate},unidadesDetalhes.unidade,recursosObrigatorios,poolRecursoId`;
      }
      
      try {
        const response = await apiClient.get('/servicos/buscar', { params: queryParams });
        return processApiResponse(response.data);
          
      } catch (error) {
        console.error('❌ [USE_SERVICOS_ANAMNESE] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && !!params?.account,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

export const useServicosFiltrados = (params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['servicos-filtrados', params],
    queryFn: async () => {
      const queryParams = sanitizeParams(params);
      
      // ✅ CORREÇÃO CRÍTICA: Adicionar select para garantir campos importantes
      if (!queryParams.select) {
        queryParams.select = 'nome descricao duracaoSessaoEmMinutos agendavel controlaSessao ativo ' +
                            'tipoDePrecificacao valorFixo pacotesDePreco metadata ' +
                            'imagemPrincipal imagens recursosVinculados recursosObrigatorios ' +
                            'usaPoolRecurso poolRecursoId quantidadeRecursosNecessaria ' +
                            'unidadesDetalhes categoria';
      } else if (!queryParams.select.includes('duracaoSessaoEmMinutos')) {
        queryParams.select = `${queryParams.select},duracaoSessaoEmMinutos`;
      }
      if (!queryParams.select.includes('usaPoolRecurso')) {
        queryParams.select = `${queryParams.select},usaPoolRecurso,poolRecursoId,quantidadeRecursosNecessaria,recursosObrigatorios`;
      }

      if (!queryParams.populate) {
        queryParams.populate = 'unidadesDetalhes.unidade,categoria,recursosVinculados,recursosObrigatorios,poolRecursoId';
      } else if (!queryParams.populate.includes('unidadesDetalhes.unidade')) {
        queryParams.populate = `${queryParams.populate},unidadesDetalhes.unidade,recursosObrigatorios,poolRecursoId`;
      }
      
      try {
        const response = await apiClient.get('/servicos/buscar', { params: queryParams });
        return processApiResponse(response.data);
          
      } catch (error) {
        console.error('❌ [USE_SERVICOS_FILTRADOS] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && !!params?.account,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

export const useServicosEstatisticas = (params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['servicos-estatisticas', params],
    queryFn: async () => {
      const queryParams = sanitizeParams(params);
      
      try {
        const response = await apiClient.get('/servicos/estatisticas', { 
          params: queryParams 
        });
        
        return response.data || { 
          success: true, 
          data: {},
          count: 0,
          totalCount: 0,
          pagination: {}
        };
      } catch (error) {
        console.error('❌ [USE_SERVICOS_ESTATISTICAS] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && !!params?.account,
    staleTime: 1000 * 60 * 2,
    retry: 1,
    ...queryOptions
  });
};

export const useUnidadesDoServico = (servicoId, options = {}) => {
  const { enabled = true } = options;
  const { data: servico, isLoading, error } = useServico(servicoId, { enabled });
  
  return {
    unidades: servico?.unidadesDetalhes || [],
    isLoading,
    error
  };
};

export const useServicoNaUnidade = (servicoId, unidadeId, options = {}) => {
  const { enabled = true } = options;
  const { data: servico, isLoading, error } = useServico(servicoId, { enabled });
  
  const unidadeDetalhe = servico?.unidadesDetalhes?.find(ud => 
    ud.unidade?.toString() === unidadeId?.toString()
  );
  
  return {
    servico,
    unidadeDetalhe,
    ativoNaUnidade: unidadeDetalhe?.ativo ?? servico?.ativo,
    precoNaUnidade: unidadeDetalhe?.precoPersonalizado,
    disponivelNaUnidade: unidadeDetalhe?.disponivelOnline ?? servico?.metadata?.disponivelOnline,
    isLoading,
    error
  };
};

// ✅ NOVO HOOK: Serviços com pool de recursos
export const useServicosComPool = (params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['servicos-com-pool', params],
    queryFn: async () => {
      const queryParams = sanitizeParams({
        usaPoolRecurso: true,
        ativo: true,
        ...params
      });
      
      if (!queryParams.select) {
        queryParams.select = 'nome descricao duracaoSessaoEmMinutos agendavel controlaSessao ativo ' +
                            'tipoDePrecificacao valorFixo metadata ' +
                            'imagemPrincipal imagens usaPoolRecurso poolRecursoId quantidadeRecursosNecessaria';
      }
      
      if (!queryParams.populate) {
        queryParams.populate = 'poolRecursoId';
      }
      
      try {
        const response = await apiClient.get('/servicos', { params: queryParams });
        return processApiResponse(response.data);
      } catch (error) {
        console.error('❌ [USE_SERVICOS_COM_POOL] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && !!params?.account,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

// ✅ NOVO HOOK: Serviços com recursos obrigatórios
export const useServicosComRecursosObrigatorios = (params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['servicos-com-recursos-obrigatorios', params],
    queryFn: async () => {
      const queryParams = sanitizeParams({
        temRecursosObrigatorios: true,
        ativo: true,
        ...params
      });
      
      if (!queryParams.select) {
        queryParams.select = 'nome descricao duracaoSessaoEmMinutos agendavel controlaSessao ativo ' +
                            'tipoDePrecificacao valorFixo metadata ' +
                            'imagemPrincipal imagens recursosObrigatorios';
      }
      
      if (!queryParams.populate) {
        queryParams.populate = 'recursosObrigatorios';
      }
      
      try {
        const response = await apiClient.get('/servicos', { params: queryParams });
        return processApiResponse(response.data);
      } catch (error) {
        console.error('❌ [USE_SERVICOS_COM_RECURSOS_OBRIGATORIOS] Erro:', error);
        throw error;
      }
    },
    enabled: enabled && !!params?.account,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    ...queryOptions
  });
};

// ==============================================================================
// MUTAÇÕES (WRITE)
// ==============================================================================

export const useMutateServicos = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  const invalidateCaches = (servicoId = null) => {
    const queriesToInvalidate = [
      SERVICOS_QUERY_KEY,
      SERVICOS_EXCLUIDOS_QUERY_KEY,
      'servicos-busca',
      'servicos-agendaveis',
      'servicos-externos',
      'servicos-internos',
      'servicos-online',
      'servicos-anamnese',
      'servicos-filtrados',
      'servicos-estatisticas',
      'servicos-unidade',
      'servicos-externos-localizacao',
      'profissionais-servico',
      USER_SERVICOS_QUERY_KEY,
      'servico-cache',
      'servico-status',
      'servicos-com-pool',
      'servicos-com-recursos-obrigatorios'
    ];

    queriesToInvalidate.forEach(queryKey => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    });

    if (servicoId) {
      queryClient.invalidateQueries({ queryKey: [SERVICO_DETAIL_QUERY_KEY, servicoId] });
      queryClient.invalidateQueries({ queryKey: ['unidades-servico', servicoId] });
      queryClient.invalidateQueries({ queryKey: ['servico-unidade', servicoId] });
      queryClient.invalidateQueries({ queryKey: ['servico-cache', servicoId] });
      queryClient.invalidateQueries({ queryKey: ['servico-status', servicoId] });
    }
  };

  const handleError = (error, defaultMessage) => {
    console.error('❌ [USE_MUTATE_SERVICOS] Erro:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    
    const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         error.message || 
                         defaultMessage;
    
    setAlert(errorMessage, 'danger');
    throw new Error(errorMessage);
  };

  const handleSuccess = (message, servicoId = null) => {
    invalidateCaches(servicoId);
    setAlert(message, 'success');
  };

  const validateServicoId = (servicoId, operation) => {
    const idToValidate = servicoId?._id || servicoId?.id || servicoId?.servicoId || servicoId;
    
    if (!idToValidate) {
      throw new Error(`ID do serviço é obrigatório para ${operation}`);
    }
    
    if (!/^[0-9a-fA-F]{24}$/.test(idToValidate)) {
      throw new Error(`ID do serviço inválido para ${operation}: ${idToValidate}`);
    }
    
    return idToValidate;
  };

  // ✅ NOVA MUTATION: Validar recursos do serviço
  const validateServicoRecursos = useMutation({
    mutationFn: async (servicoData) => {
      const dadosPreparados = prepararDadosServico(servicoData);
      const response = await apiClient.post('/servicos/validar-recursos', dadosPreparados);
      return response.data;
    },
    onError: (error) => {
      handleError(error, 'Erro ao validar recursos do serviço');
    },
  });

  const restaurarServico = useMutation({
    mutationFn: async (servicoId) => {
      const idToRestore = servicoId?._id || servicoId?.id || servicoId?.servicoId || servicoId;
      validateServicoId(idToRestore, 'restauração');
      
      const response = await apiClient.patch(`/servicos/${idToRestore}/restaurar`);
      return response.data;
    },
    onSuccess: (response, servicoId) => {
      const idToInvalidate = servicoId?._id || servicoId?.id || servicoId?.servicoId || servicoId;
      handleSuccess('Serviço restaurado com sucesso!', idToInvalidate);
    },
    onError: (error) => handleError(error, 'Erro ao restaurar serviço'),
  });

  const hardDeleteServico = useMutation({
    mutationFn: async (servicoId) => {
      const idToDelete = servicoId?._id || servicoId?.id || servicoId?.servicoId || servicoId;
      validateServicoId(idToDelete, 'exclusão permanente');
      
      const response = await apiClient.delete(`/servicos/${idToDelete}/hard-delete`);
      return response.data;
    },
    onSuccess: (response, servicoId) => {
      const idToInvalidate = servicoId?._id || servicoId?.id || servicoId?.servicoId || servicoId;
      handleSuccess('Serviço excluído permanentemente!', idToInvalidate);
    },
    onError: (error) => handleError(error, 'Erro ao excluir permanentemente'),
  });

  const atualizarCacheServico = useMutation({
    mutationFn: async (servicoId) => {
      const idToUpdate = servicoId?._id || servicoId?.id || servicoId?.servicoId || servicoId;
      validateServicoId(idToUpdate, 'atualização de cache');
      
      const response = await apiClient.put(`/servicos/${idToUpdate}/cache`);
      return response.data;
    },
    onSuccess: (response, servicoId) => {
      const idToInvalidate = servicoId?._id || servicoId?.id || servicoId?.servicoId || servicoId;
      handleSuccess('Cache do serviço atualizado com sucesso!', idToInvalidate);
    },
    onError: (error) => handleError(error, 'Erro ao atualizar cache'),
  });

  const createServico = useMutation({
    mutationFn: async (servicoData) => {
      const dadosPreparados = prepararDadosServico(servicoData);
      
      const response = await apiClient.post('/servicos', dadosPreparados, {
        timeout: 20000
      });
      return response.data;
    },
    onSuccess: (response) => {
      const servicoId = response.data?._id || response.data?.id;
      handleSuccess('Serviço criado com sucesso!', servicoId);
    },
    onError: (error) => handleError(error, 'Erro ao criar serviço'),
  });

  const updateServico = useMutation({
    mutationFn: async ({ id, servicoData }) => {
      const servicoId = id?._id || id?.id || id?.servicoId || id;
      
      validateServicoId(servicoId, 'atualização');
      
      const dadosPreparados = prepararDadosServico(servicoData);
      
      const response = await apiClient.put(`/servicos/${servicoId}`, dadosPreparados, {
        timeout: 20000
      });
      return response.data;
    },
    onSuccess: (response, variables) => {
      const servicoId = variables.id?._id || variables.id?.id || variables.id?.servicoId || variables.id;
      handleSuccess('Serviço atualizado com sucesso!', servicoId);
    },
    onError: (error) => handleError(error, 'Erro ao atualizar serviço'),
  });

  const deleteServico = useMutation({
    mutationFn: async (servicoId) => {
      const idToDelete = servicoId?._id || servicoId?.id || servicoId?.servicoId || servicoId;
      validateServicoId(idToDelete, 'exclusão');
      
      const response = await apiClient.delete(`/servicos/${idToDelete}`);
      return response.data;
    },
    onSuccess: (response, servicoId) => {
      const idToInvalidate = servicoId?._id || servicoId?.id || servicoId?.servicoId || servicoId;
      handleSuccess('Serviço removido com sucesso!', idToInvalidate);
    },
    onError: (error) => handleError(error, 'Erro ao remover serviço'),
  });

  const createUserServico = useMutation({
    mutationFn: async (userServicoData) => {
      const dadosPreparados = prepararDadosServico(userServicoData);
      
      const response = await apiClient.post('/relationships/user-servicos', dadosPreparados);
      return response.data;
    },
    onSuccess: (response) => {
      const userServicoId = response.data?._id || response.data?.id;
      handleSuccess('Vínculo criado com sucesso!', userServicoId);
      
      queryClient.invalidateQueries({ queryKey: [USER_SERVICOS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['profissionais-servico'] });
    },
    onError: (error) => handleError(error, 'Erro ao criar vínculo'),
  });

  const updateUserServico = useMutation({
    mutationFn: async ({ id, userServicoData }) => {
      const userServicoId = id?._id || id?.id || id;
      
      if (!userServicoId) {
        throw new Error('ID do vínculo é obrigatório');
      }
      
      const dadosPreparados = prepararDadosServico(userServicoData);
      
      const response = await apiClient.put(`/relationships/user-servicos/${userServicoId}`, dadosPreparados);
      return response.data;
    },
    onSuccess: (response, variables) => {
      const userServicoId = variables.id?._id || variables.id?.id || variables.id;
      handleSuccess('Vínculo atualizado com sucesso!', userServicoId);
      
      queryClient.invalidateQueries({ queryKey: [USER_SERVICOS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['profissionais-servico'] });
    },
    onError: (error) => handleError(error, 'Erro ao atualizar vínculo'),
  });

  const deleteUserServico = useMutation({
    mutationFn: async (userServicoId) => {
      const idToDelete = userServicoId?._id || userServicoId?.id || userServicoId;
      
      if (!idToDelete) {
        throw new Error('ID do vínculo é obrigatório');
      }
      
      const response = await apiClient.delete(`/relationships/user-servicos/${idToDelete}`);
      return response.data;
    },
    onSuccess: (response, userServicoId) => {
      const idToInvalidate = userServicoId?._id || userServicoId?.id || userServicoId;
      handleSuccess('Vínculo removido com sucesso!', idToInvalidate);
      
      queryClient.invalidateQueries({ queryKey: [USER_SERVICOS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['profissionais-servico'] });
    },
    onError: (error) => handleError(error, 'Erro ao remover vínculo'),
  });

  const uploadServicoImage = useMutation({
    mutationFn: async ({ servicoId, file }) => {
      const idToUpload = servicoId?._id || servicoId?.id || servicoId?.servicoId || servicoId;
      validateServicoId(idToUpload, 'upload de imagem');
      
      if (!file) {
        throw new Error('Arquivo de imagem é obrigatório');
      }

      const formData = new FormData();
      formData.append('imagem', file);
      
      const response = await apiClient.post(`/servicos/${idToUpload}/imagens`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000,
      });
      
      return response.data;
    },
    onSuccess: (response, variables) => {
      const idToInvalidate = variables.servicoId?._id || variables.servicoId?.id || variables.servicoId?.servicoId || variables.servicoId;
      handleSuccess('Imagem do serviço atualizada com sucesso!', idToInvalidate);
    },
    onError: (error) => handleError(error, 'Erro ao fazer upload da imagem'),
  });

  const deleteServicoImage = useMutation({
    mutationFn: async ({ servicoId, imageId }) => {
      const idToDelete = servicoId?._id || servicoId?.id || servicoId?.servicoId || servicoId;
      validateServicoId(idToDelete, 'exclusão de imagem');
      
      if (!imageId) {
        throw new Error('ID da imagem é obrigatório');
      }

      const response = await apiClient.delete(`/servicos/${idToDelete}/imagens/${imageId}`);
      return response.data;
    },
    onSuccess: (response, variables) => {
      const idToInvalidate = variables.servicoId?._id || variables.servicoId?.id || variables.servicoId?.servicoId || variables.servicoId;
      handleSuccess('Imagem do serviço removida com sucesso!', idToInvalidate);
    },
    onError: (error) => handleError(error, 'Erro ao remover imagem'),
  });

  const setServicoImagemPrincipal = useMutation({
    mutationFn: async ({ servicoId, tipo, refId }) => {
      const idToUpdate = servicoId?._id || servicoId?.id || servicoId?.servicoId || servicoId;
      validateServicoId(idToUpdate, 'definição de imagem principal');
      
      if (!tipo || !refId) {
        throw new Error('Tipo e ID de referência são obrigatórios');
      }

      const response = await apiClient.patch(`/servicos/${idToUpdate}/imagens/principal`, {
        tipo,
        refId
      });
      
      return response.data;
    },
    onSuccess: (response, variables) => {
      const idToInvalidate = variables.servicoId?._id || variables.servicoId?.id || variables.servicoId?.servicoId || variables.servicoId;
      handleSuccess('Imagem principal definida com sucesso!', idToInvalidate);
    },
    onError: (error) => handleError(error, 'Erro ao definir imagem principal'),
  });

  const validateServicoPrecificacao = useMutation({
    mutationFn: async (servicoData) => {
      const dadosPreparados = prepararDadosServico(servicoData);
      const response = await apiClient.post('/servicos/validar-precificacao', dadosPreparados);
      return response.data;
    },
    onError: (error) => {
      throw error;
    },
  });

  const isLoading = {
    create: createServico.isPending || false,
    update: updateServico.isPending || false,
    delete: deleteServico.isPending || false,
    restaurar: restaurarServico.isPending || false,
    hardDelete: hardDeleteServico.isPending || false,
    atualizarCache: atualizarCacheServico.isPending || false,
    createUserServico: createUserServico.isPending || false,
    updateUserServico: updateUserServico.isPending || false,
    deleteUserServico: deleteUserServico.isPending || false,
    uploadImage: uploadServicoImage.isPending || false,
    deleteImage: deleteServicoImage.isPending || false,
    setMainImage: setServicoImagemPrincipal.isPending || false,
    validatePrecificacao: validateServicoPrecificacao.isPending || false,
    validateRecursos: validateServicoRecursos.isPending || false
  };

  const isPending = Object.values(isLoading).some(status => status);

  return {
    createServico,
    updateServico,
    deleteServico,
    restaurarServico,
    hardDeleteServico,
    atualizarCacheServico,
    createUserServico,
    updateUserServico,
    deleteUserServico,
    uploadServicoImage,
    deleteServicoImage,
    setServicoImagemPrincipal,
    validateServicoPrecificacao,
    validateServicoRecursos,
    isPending,
    isLoading,
    invalidateCaches,
    forceRefetch: () => invalidateCaches(),
    validateServicoId,
  };
};

// ✅ Hook gerenciador completo
export const useServicosManager = (params = {}, options = {}) => {
  const queryClient = useQueryClient();
  const mutations = useMutateServicos();
  
  const { data: servicosData, isLoading: loadingServicos } = useServicos(params, options);
  const { data: servicosExcluidosData, isLoading: loadingExcluidos } = useServicosExcluidos(params, options);
  const { data: servicosExternosData, isLoading: loadingExternos } = useServicosExternos(params, options);
  const { data: servicosInternosData, isLoading: loadingInternos } = useServicosInternos(params, options);
  const { data: servicosAgendaveisData, isLoading: loadingAgendaveis } = useServicosAgendaveis(params, options);
  const { data: servicosComPoolData, isLoading: loadingComPool } = useServicosComPool(params, options);
  const { data: servicosComRecursosObrigatoriosData, isLoading: loadingComRecursosObrigatorios } = useServicosComRecursosObrigatorios(params, options);
  const { data: estatisticasData, isLoading: loadingEstatisticas } = useServicosEstatisticas(params, options);
  
  const isLoading = loadingServicos || loadingExcluidos || loadingExternos || loadingInternos || loadingAgendaveis || loadingComPool || loadingComRecursosObrigatorios || loadingEstatisticas;
  
  const extractData = (responseData) => {
    if (!responseData) return [];
    if (Array.isArray(responseData)) return responseData;
    if (responseData.data !== undefined) return responseData.data || [];
    if (responseData.success && responseData.data) return responseData.data;
    return [];
  };
  
  const extractEstatisticas = (responseData) => {
    if (!responseData) return {};
    if (responseData.data !== undefined) return responseData.data || {};
    return responseData || {};
  };

  return {
    servicos: extractData(servicosData),
    servicosExcluidos: extractData(servicosExcluidosData),
    servicosExternos: extractData(servicosExternosData),
    servicosInternos: extractData(servicosInternosData),
    servicosAgendaveis: extractData(servicosAgendaveisData),
    servicosComPool: extractData(servicosComPoolData),
    servicosComRecursosObrigatorios: extractData(servicosComRecursosObrigatoriosData),
    estatisticas: extractEstatisticas(estatisticasData),
    isLoading,
    isLoadingServicos: loadingServicos,
    isLoadingExcluidos: loadingExcluidos,
    isLoadingExternos: loadingExternos,
    isLoadingInternos: loadingInternos,
    isLoadingAgendaveis: loadingAgendaveis,
    isLoadingComPool: loadingComPool,
    isLoadingComRecursosObrigatorios: loadingComRecursosObrigatorios,
    isLoadingEstatisticas: loadingEstatisticas,
    ...mutations,
    refetchAll: () => {
      queryClient.invalidateQueries({ queryKey: [SERVICOS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SERVICOS_EXCLUIDOS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['servicos-externos'] });
      queryClient.invalidateQueries({ queryKey: ['servicos-internos'] });
      queryClient.invalidateQueries({ queryKey: ['servicos-agendaveis'] });
      queryClient.invalidateQueries({ queryKey: ['servicos-estatisticas'] });
      queryClient.invalidateQueries({ queryKey: ['servicos-com-pool'] });
      queryClient.invalidateQueries({ queryKey: ['servicos-com-recursos-obrigatorios'] });
      queryClient.invalidateQueries({ queryKey: [USER_SERVICOS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['profissionais-servico'] });
      queryClient.invalidateQueries({ queryKey: ['servico-cache'] });
      queryClient.invalidateQueries({ queryKey: ['servico-status'] });
    }
  };
};

export default useServicos;